import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, Filter, Search, ShoppingCart, Eye, Star, Clock, Users } from 'lucide-react';
import { Product } from '../types';
import apiService from '../services/api';
import SEO from '../components/SEO';
import { toProductImageUrl } from '../utils/imageUrl';

const Products: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [arsRate, setArsRate] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');

  const categories = [
    { value: 'all', label: 'Todos los Productos' },
    { value: 'comercial', label: 'Bolsas Comerciales' },
    { value: 'industrial', label: 'Bolsas Industriales' },
    { value: 'domestico', label: 'Bolsas Domésticas' }
  ];

  const sortOptions = [
    { value: 'name', label: 'Nombre' },
    { value: 'category', label: 'Categoría' },
    { value: 'thickness', label: 'Grosor' },
    { value: 'deliveryDays', label: 'Tiempo de Entrega' }
  ];

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoadError(null);
        const [response, currency] = await Promise.all([
          apiService.getProducts(),
          apiService.getCurrencyRates().catch(() => null)
        ]);
        setProducts(response.products);
        if (currency?.rates?.sell) setArsRate(Number(currency.rates.sell));
        setFilteredProducts(response.products);
      } catch (error) {
        console.error('Error fetching products:', error);
        setLoadError('No se pudieron cargar los productos. Verifica la conexión con el backend.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    let filtered = products;

    // Filtrar por categoría
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    // Filtrar por término de búsqueda
    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.material.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Ordenar
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'category':
          return a.category.localeCompare(b.category);
        case 'thickness':
          return b.thickness - a.thickness;
        case 'deliveryDays':
          return a.deliveryDays - b.deliveryDays;
        default:
          return 0;
      }
    });

    setFilteredProducts(filtered);
  }, [products, selectedCategory, searchTerm, sortBy]);

  const getUnitsPerPack = (productName?: string) => {
    const match = String(productName || '').match(/x\s*(\d+)\s*unidades?/i);
    const units = match ? Number(match[1]) : 1;
    return Number.isFinite(units) && units > 0 ? units : 1;
  };

  const getPackPriceARS = (product: Product) => {
    const usd = Number(product.priceUSD || 0);
    if (!product.showPrice || !usd || !arsRate) return 'Consultar precio por cantidad';
    const ars = usd * getUnitsPerPack(product.name) * arsRate;
    return `$${Math.round(ars).toLocaleString('es-AR')} ARS por pack`;
  };
  const getCategoryLabel = (category: string) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'comercial':
        return 'bg-blue-100 text-blue-800';
      case 'industrial':
        return 'bg-green-100 text-green-800';
      case 'domestico':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/4 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-lg shadow-md p-6">
                  <div className="h-48 bg-gray-300 rounded mb-4"></div>
                  <div className="h-4 bg-gray-300 rounded mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded w-3/4 mb-4"></div>
                  <div className="h-8 bg-gray-300 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Productos"
        description="Catálogo completo de bolsas de polietileno LDPE. Bolsas comerciales, industriales y domésticas de alta calidad. Especificaciones técnicas, precios y cotizaciones."
        keywords="catálogo productos, bolsas plásticas, polietileno LDPE, bolsas comerciales, bolsas industriales, especificaciones técnicas, cotizaciones"
      />
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Nuestros Productos
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Descubre nuestra amplia gama de bolsas de plástico LDPE personalizadas. 
              Todos los anchos, largos y colores con la mejor calidad.
            </p>
          </div>
        </div>
      </div>

      {/* Filtros y Búsqueda */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Búsqueda */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar productos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Filtro por categoría */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {categories.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>

            {/* Ordenar */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  Ordenar por: {option.label}
                </option>
              ))}
            </select>

            {/* Botón de filtros */}
            <button className="flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
              <Filter className="h-5 w-5 mr-2" />
              Filtros
            </button>
          </div>
        </div>

        {/* Resultados */}
        <div className="mb-6">
          <p className="text-gray-600">
            Mostrando {filteredProducts.length} de {products.length} productos
          </p>
          {loadError && (
            <p className="mt-2 text-sm font-medium text-red-600">
              {loadError}
            </p>
          )}
        </div>

        {/* Grid de productos */}
        {filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No se encontraron productos
            </h3>
            <p className="text-gray-600">
              Intenta ajustar los filtros o términos de búsqueda
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product) => (
              <div key={product.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                {/* Imagen del producto */}
                <div className="h-64 bg-white flex items-center justify-center overflow-hidden">
                  {product.image ? (
                    <img
                      src={toProductImageUrl(product.image)}
                      alt={product.name}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <Package className="h-16 w-16 text-gray-400" />
                  )}
                </div>

                <div className="p-6">
                  {/* Categoría */}
                  <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full mb-3 ${getCategoryColor(product.category)}`}>
                    {getCategoryLabel(product.category)}
                  </span>

                  {/* Nombre del producto */}
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {product.name}
                  </h3>

                  {/* Descripción */}
                  <p className="text-gray-600 mb-4 line-clamp-2">
                    {product.description || 'Bolsas de plástico LDPE de alta calidad'}
                  </p>

                  {/* Especificaciones */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <Package className="h-4 w-4 mr-2" />
                      <span>Material: {product.material}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="w-4 h-4 mr-2">📏</span>
                      <span>Grosor: {product.thickness} micrones</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="w-4 h-4 mr-2">📐</span>
                      <span>{Math.round(Number(product.width) * 100)} cm x {Math.round(Number(product.length) * 100)} cm</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Clock className="h-4 w-4 mr-2" />
                      <span>Entrega: {Number(product.deliveryDays) === 0 ? 'Inmediata' : `${product.deliveryDays} dias`}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Users className="h-4 w-4 mr-2" />
                      <span>Mínimo: {product.minQuantity.toLocaleString()} unidades</span>
                    </div>
                  </div>

                  {/* Precio */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-lg font-semibold text-gray-600">
                        {getPackPriceARS(product)}
                      </div>
                    </div>
                    {product.isFeatured && (
                      <Star className="h-5 w-5 text-yellow-500 fill-current" />
                    )}
                  </div>

                  {/* Botones de acción */}
                  <div className="flex space-x-2">
                    <Link
                      to={`/productos/${product.id}`}
                      className="flex-1 flex items-center justify-center px-4 py-2 border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Ver Detalles
                    </Link>
                    <Link
                      to={`/contacto?subject=cotizacion&product=${encodeURIComponent(product.name)}`}
                      className="flex-1 flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      Cotizar
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-12 text-center">
          <div className="bg-primary-600 rounded-lg p-8 text-white">
            <h2 className="text-2xl font-bold mb-4">
              ¿No encuentras lo que buscas?
            </h2>
            <p className="text-primary-100 mb-6">
              Fabricamos bolsas personalizadas según tus especificaciones exactas
            </p>
            <Link
              to="/contacto"
              className="inline-flex items-center px-6 py-3 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Solicitar Cotización Personalizada
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;

